import StaticShipStatic from "@/repositories/ships/ShipRepoStatic";
import store from "@/store/index";
import ConfigurationService from "./ConfigurationService";
import { config } from "@/utils/configuration/config";
import ShipRepoFirebase from "@/repositories/ships/ShipRepoFirebase";
import ShipTypeService from "@/services/ShipTypeService";
import CalcRequiredEEXIService from "@/services/CalcRequiredEEXIService";
import Ship from "@/model/Ship";
import CarbonService from "./CarbonService";
import ModificationService from "@/services/Neobuq/ModificationService";

// Servicio para tratar todos los casos de uso relacionados con los buques
class ShipService {
  private static instance: ShipService;
  private shipsRepo: any;
  private configurationService: ConfigurationService;
  private modificationService : ModificationService;
  private ships: Array<Ship>;

  // Constructor que instancia el repositorio dependiendo de la configuración e instancia
  // el servicio para tratar las configuraciones
  private constructor() {
    //console.log("ShipService - constructor");

    // Se obtiene el valor de la configuración para saber si hay que utilizar firebase o se
    // van a utilizar datos estáticos de prueba
    if (!config.app.useFirebase) {
      //console.log("STATIC SHIP STATIC");
      this.shipsRepo = new StaticShipStatic();
    } else {
      //console.log("SHIP REPO FIREBASE");
      this.shipsRepo = new ShipRepoFirebase();
    }

    // Se instancia el servicio para tratar las configuraciones del buque
    this.configurationService = ConfigurationService.getInstance();
    this.modificationService = ModificationService.getInstance();

    this.ships = new Array<Ship>();
  }

  public static getInstance(): ShipService {
    //console.log("ShipService - getInstance");
    if (!ShipService.instance) {
      ShipService.instance = new ShipService();
    }

    return ShipService.instance;
  }

  // Métido que obtiene los buques del repositorio y las añade al array que se pasa como parámetro
  public async getShips(ships: Array<Ship>) {
    //console.log("ShipService - getShips.length", ships.length)

    if (this.ships.length == 0) {
      await this.shipsRepo.getShips(ships, this);
      //console.log("ShipService - getShips result", ships)
      this.ships = ships;
    } else {
      this.ships.forEach((ship) => ships.push(ship));
    }

    //console.log("ShipService - getShips result", ships)
  }

  public clearShips() {
    // Se eliminan todoso los buques que tenemos en el array
    this.ships.splice(0);
  }

  public getShip(shipId: string): Ship | undefined {
    //console.log("Se quiere buscar el buque con id", shipId, "dentro de la lista de buques", this.ships);
    return this.ships.find((ship) => ship.id === shipId);
  }

  // Este método lo invoca el repositorio cuando detecta que se debe añadir un buque
  public addNewShipFromRepo(ships, id, data) {
    //console.log("ShipService-addNewShipFromRepo. Añadido ship", id, data.name);

    const user = store.getters.currentUser;

    const ship = this.getShipFromFirebase(id, data);

    ShipTypeService.AddShipTypeToShip(data.shipType, ship as any);

    const configurations = [];
    const modifications = [];
    ship.configurations = configurations;
    ship.modifications = modifications;
    this.configurationService.getConfigurationsForShip(ship);
    this.modificationService.getModificationsForShip(ship);

    CarbonService.getInstance().getCarbonData(ship);

    //console.log("ShipService-addNewShipFromRepo", user);
    //console.log("ShipService-addNewShipFromRepo", ship);
    if (String(ship.author) == String(user.uid) || user.administrator == true) {
      ships.push(ship);
    }
  }

  // Este método lo invoca el repositorio cuando detecta que se debe modificar un buque
  public modifyShipFromRepo(ships, id: string, data: any) {
    //console.log("ShipService-modifyShipFromRepo. Modificado ship", id, data);
    const pos = ships.map((val) => val.id).indexOf(id);

    const newShipObj = this.getShipFromFirebase(id, data);

    ShipTypeService.AddShipTypeToShip(data.shipType, newShipObj as any);

    ConfigurationService.getInstance().getConfigurationsForShip(newShipObj);

    CarbonService.getInstance().getCarbonData(newShipObj);

    this.calcAttainedFromConfigurations(id);

    ships[pos] = newShipObj;

    if (id === store.getters.currentShip.id) {
      //console.log("Cambiamos el buque porque está en edición", newShipObj);
      store.commit("SET_SHIPS_CURRENT_SHIP", newShipObj);
    }
  }

  // Este método lo invoca el repositorio cuando detecta que se debe eliminar un buque
  public removeShipFromRepo(ships: Array<Ship>, id: string) {
    const pos = ships.map((val) => val.id).indexOf(id);
    if (pos >= 0) {
      ships.splice(pos, 1);
      // console.log("ShipService-removeShipFromRepo. Removed Ship: ", id);
    }
  }

  // Este método del servicio se invoca desde la vista cuando se ha seleccionado un buque
  public selectedShip(ship: Ship) {
    //console.log("Selectedship", ship);
    store.commit("SET_SHIPS_CURRENT_SHIP", ship);
  }

  public calcRequiredEEXI(ship) {
    let result = false;
    const { requiredEEXI, logElement } = CalcRequiredEEXIService.calcRequiredEEXI(ship);

    //console.log("RESULT: ", requiredEEXI, logElement);

    if (requiredEEXI) {
      ship.requiredEEXI = requiredEEXI;
      result = true;
    } else {
      ship.requiredEEXI = 0;
    }

    if (logElement.length > 0) {
      ship.logElement = logElement;
    }

    return result;
  }

  public calcIsEEDIUsingDates(ship) {
    return CalcRequiredEEXIService.isEEDIUsingDates(
      ship.keelLaidDownDate,
      ship.contractSignInDate,
      ship.shipDeliveredDate,
      []
    );
  }

  public calcAttainedFromConfigurations(shipId: string) {
    let result: string = "-";
    const ship: Ship | undefined = this.getShip(shipId);
    if (ship) {
      ship.configurations.forEach((config) => {
        if (config.isBase) {
          result = config.attainedEEXI + " (" + config.name + ")";
        }
      });

      ship.attainedEEXI = result;
    }
  }

  private getShipFromFirebase(id, data) {
    const ship = {
      id: id,
      imo: data.imo,
      name: data.name,
      requiredEEXI: data.requiredEEXI,
      attainedEEXI: "-",
      shipType: {},
      iceClassType: data.iceClassType ? data.iceClassType : "",
      author: data.author,

      keelLaidDownDate: this.getDateFromFirestoreValue(data.keelLaidDownDate),
      contractSignInDate: this.getDateFromFirestoreValue(data.contractSignInDate),
      shipDeliveredDate: this.getDateFromFirestoreValue(data.shipDeliveredDate),

      gt: data.gt,
      dwt: data.dwt,
      loa: data.loa,
      lpp: data.lpp,
      cb: data.cb,
      manualVref: data.manualVref,
      speedPowerTests: data.speedPowerTests,
      dwtsService: data.dwtsService,
      psService: data.psService,
      vsService: data.vsService,
      bs: data.bs,
      ds: data.ds,

      displacementVolume: data.displacementVolume,

      // has_vse
      lwtReferenceDesign: data.lwtReferenceDesign ? data.lwtReferenceDesign : 0,
      lwtEnhancedDesign: data.lwtEnhancedDesign ? data.lwtEnhancedDesign : 0,
      //capacity
      capacityCorrection: data.capacityCorrection ? data.capacityCorrection : false,

      cranes: data.cranes ? data.cranes : [],

      has_sideloaders: data.has_sideloaders ? data.has_sideloaders : false,
      capacityNoSideloaders: data.capacityNoSideloaders ? data.capacityNoSideloaders : 0,
      capacitySideloaders: data.capacitySideloaders ? data.capacitySideloaders : 0,

      has_roro_ramps: data.has_roro_ramps ? data.has_roro_ramps : false,
      capacityNoRORORamp: data.capacityNoRORORamp ? data.capacityNoRORORamp : 0,
      capacityRORORamp: data.capacityRORORamp ? data.capacityRORORamp : 0,

      structuralEnhancement: data.structuralEnhancement ? data.structuralEnhancement : 0,

      has_csr: data.has_csr ? data.has_csr : false,
      dwtCsr: data.dwtCsr ? data.dwtCsr : 0,
      lwtCsr: data.lwtCsr ? data.lwtCsr : 0,

      vTanks: data.vTanks ? data.vTanks : 0,

      fc: data.fc,
      fi: data.fi,
      fi_csr: data.fi,
      fi_ice: data.fi,
      fi_vse: data.fi,

      highSpeedCruise: data.highSpeedCruise,

      data: data,
      configurations: [],
      modifications: [],

      fuelConsumption : data.fuelConsumption,
      totalDistance : data.totalDistance ? data.totalDistance : 0,
      oldPrewrittenCII : data.oldPrewrittenCII,
      oldAttainedCII : data.oldAttainedCII ? data.oldAttainedCII : 0,
      anualPrewrittenCII: data.anualPrewrittenCII,

      baseCII : data.baseCII ? data.baseCII : [],
      baseFuelEU : data.baseFuelEU ? data.baseFuelEU : [],
      baseETS : data.baseETS ? data.baseETS : [],
      selectedBaseCII : data.selectedBaseCII ? data.selectedBaseCII : "",
      selectedBaseFuelEU : data.selectedBaseFuelEU ? data.selectedBaseFuelEU : 0,
      selectedBaseETS : data.selectedBaseETS ? data.selectedBaseETS : 0,
      dataForCII : data.dataForCII ? data.dataForCII : null,
      dataForETS : data.dataForETS ? data.dataForETS : null,
      dataForFuelEU : data.dataForFuelEU ? data.dataForFuelEU : null,

      isNEOBUQ : data.isNEOBUQ ? data.isNEOBUQ : false

    };
    return ship;
  }

  //No se asegura de que estes logueado.
  private getShipToFirebase(ship) {
    const user = store.getters.currentUser;

    //SOLO NEOBUQ: fuelConsum es un tipo que no existe en FIREBASE, hay que ponerlo de otra manera; lo hacemos así:
    const arrayAuxFuelConsumption = [{}]

    if(ship.fuelConsumption != null || ship.fuelConsumption != undefined){
      //console.log("No está vacío: " + ship.fuelConsumption)
      let auxItem = {}

      for(let i=0; i< ship.fuelConsumption.length; i++){
  
        auxItem = {
          fuel: {
            id: ship.fuelConsumption[i].fuel.id,
            name : ship.fuelConsumption[i].fuel.name,
            code : ship.fuelConsumption[i].fuel.code,
            lcv : ship.fuelConsumption[i].fuel.lcv,
            co2eqWtT : ship.fuelConsumption[i].fuel.co2eqWtT,
  
            //diccionario para los cfs, que van a ser siempre CO2, CH4 y N2O
            cfs : ship.fuelConsumption[i].fuel.cfs,
            cslip : ship.fuelConsumption[i].fuel.cslip,
            rwd : ship.fuelConsumption[i].fuel.rwd,
  
            for_cii : ship.fuelConsumption[i].fuel.for_cii,
            cii_cf : ship.fuelConsumption[i].fuel.cii_cf
          },
          consumption: ship.fuelConsumption[i].consumption
        }
  
        arrayAuxFuelConsumption.push(auxItem)
      }
  
      //quitamos el 1º item, que está vacío
      arrayAuxFuelConsumption.splice(0, 1);
    }

    const result = {
      imo: ship.imo ? ship.imo : undefined,
      name: ship.name ? ship.name : undefined,
      requiredEEXI: ship.requiredEEXI ? ship.requiredEEXI : null,
      shipType: ship.shipType ? ship.shipType.id : undefined,
      iceClassType: ship.iceClassType ? ship.iceClassType : { code: "NONE", name: "None" },
      author: user.uid ? user.uid : undefined,

      keelLaidDownDate: ship.keelLaidDownDate ? ship.keelLaidDownDate : null,
      contractSignInDate: ship.contractSignInDate ? ship.contractSignInDate : null,
      shipDeliveredDate: ship.shipDeliveredDate ? ship.shipDeliveredDate : null,

      gt: ship.gt ? ship.gt : null,
      dwt: ship.dwt,
      lpp: ship.lpp ? ship.lpp : null,
      loa: ship.loa ? ship.loa : null,
      cb: ship.cb ? ship.cb : null,
      manualVref: ship.manualVref ? ship.manualVref : null,
      speedPowerTests: ship.speedPowerTests ? ship.speedPowerTests : false,
      dwtsService: ship.dwtsService ? ship.dwtsService : null,
      psService: ship.psService ? ship.psService : null,
      vsService: ship.vsService ? ship.vsService : null,
      bs: ship.bs ? ship.bs : null,
      ds: ship.ds ? ship.ds : null,

      displacementVolume: ship.displacementVolume ? ship.displacementVolume : null,

      // has_vse
      lwtReferenceDesign: ship.lwtReferenceDesign ? ship.lwtReferenceDesign : null,
      lwtEnhancedDesign: ship.lwtEnhancedDesign ? ship.lwtEnhancedDesign : null,
      capacity: ship.capacity ? ship.capacity : null,
      capacityCorrection: ship.capacityCorrection ? ship.capacityCorrection : false,

      cranes: ship.cranes ? ship.cranes : [],

      has_sideloaders: ship.has_sideloaders ? ship.has_sideloaders : false,
      capacityNoSideloaders: ship.capacityNoSideloaders ? ship.capacityNoSideloaders : null,
      capacitySideloaders: ship.capacitySideloaders ? ship.capacitySideloaders : null,

      has_roro_ramps: ship.has_roro_ramps ? ship.has_roro_ramps : false,
      capacityNoRORORamp: ship.capacityNoRORORamp ? ship.capacityNoRORORamp : null,
      capacityRORORamp: ship.capacityRORORamp ? ship.capacityRORORamp : null,

      structuralEnhancement: ship.structuralEnhancement ? ship.structuralEnhancement : false,

      has_csr: ship.has_csr ? ship.has_csr : false,
      dwtCsr: ship.dwtCsr ? ship.dwtCsr : null,
      lwtCsr: ship.lwtCsr ? ship.lwtCsr : null,
      vTanks: ship.vTanks ? ship.vTanks : null,

      fc: ship.fc ? ship.fc : null,
      fi_csr: ship.fi_csr ? ship.fi_csr : null,
      fi_ice: ship.fi_ice ? ship.fi_ice : null,
      fi_vse: ship.fi_vse ? ship.fi_vse : null,

      v: ship.v ? ship.v : null,

      isChemicalTanker: ship.isChemicalTanker ? ship.isChemicalTanker : null,
      csr: ship.csr ? ship.csr : null,

      highSpeedCruise: ship.highSpeedCruise ? ship.highSpeedCruise : false,

      //VARIABLES NEOBUQ
      fuelConsumption : arrayAuxFuelConsumption,
      totalDistance : ship.totalDistance ? ship.totalDistance : null,
      oldPrewrittenCII : ship.oldPrewrittenCII ? ship.oldPrewrittenCII : null,
      oldAttainedCII : ship.oldAttainedCII ? ship.oldAttainedCII : null,
      anualPrewrittenCII : ship.anualPrewrittenCII ? ship.anualPrewrittenCII: null,

      baseCII : ship.baseCII ? ship.baseCII : null,
      baseFuelEU : ship.baseFuelEU ? ship.baseFuelEU : null,
      baseETS : ship.baseETS ? ship.baseETS : null,
      selectedBaseCII : ship.selectedBaseCII ? ship.selectedBaseCII : null,
      selectedBaseFuelEU : ship.selectedBaseFuelEU ? ship.selectedBaseFuelEU : null,
      selectedBaseETS : ship.selectedBaseETS ? ship.selectedBaseETS : null,
      dataForCII : ship.dataForCII ? ship.dataForCII : null,
      dataForETS : ship.dataForETS ? ship.dataForETS : null,
      dataForFuelEU : ship.dataForFuelEU ? ship.dataForFuelEU : null,

      isNEOBUQ : ship.isNEOBUQ ? ship.isNEOBUQ : false,

    };

    // console.log("ShipService.getShipToFirebase. keelLaidDownDate", result.keelLaidDownDate)

    return result;
  }

  public getDateFromFirestoreValue(dateFromFirebase) {
    let date: any = null;
    if (dateFromFirebase) {
      date = new Date(Date.UTC(1970, 0, 1));
      date.setUTCSeconds(dateFromFirebase ? dateFromFirebase.seconds : 0);
    }
    return date;
  }

  public saveShip(ship) {
    //console.log("ShipService-saveShip", ship);
    const shipToFirebase = this.getShipToFirebase(ship);
    //console.log(shipToFirebase)
    // console.log(ship.modifications)
    // ship.modifications.forEach( (mod) => {
    //   ModificationService.getInstance().saveModification(
    //     ship.id,
    //     mod
    //   );
    // })

    // Vamos a comprobar que alguna de las tres fechas no es nula
    if (
      shipToFirebase.keelLaidDownDate == null &&
      shipToFirebase.contractSignInDate == null &&
      shipToFirebase.shipDeliveredDate == null
    ) {
      return "Al menos una de las tres fechas debe estar rellena";
    }

    // Si el buque tiene id, es un buque que hay que actualizar. EOC se crea como nuevo
    const mustBeEdited = ship.id != "" && ship.id != null;

    if (mustBeEdited) {
      return this.shipsRepo.editShip(ship.id, shipToFirebase);
    } else {
      return this.shipsRepo.saveNewShip(shipToFirebase);
    }
  }

  public deleteShip(shipId) {
    this.shipsRepo.deleteShip(shipId);
  }
}

export default ShipService;
